import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'

import { useConfirmModal } from '@maxus/website/components'
import { env } from '@maxus/website/constants'
import { buildFeishuAppLink, isFeishuEnv } from '@maxus/website/lib'
import { isMobile } from '@maxus/website/utils'

// 「是否已尝试拉起飞书」标记：每个会话只提示一次，取消后不再打扰。
const FEISHU_APPLINK_ATTEMPTED_KEY = 'feishu_applink_attempted'

const hasAttemptedFeishuAppLink = (): boolean => {
  try {
    return sessionStorage.getItem(FEISHU_APPLINK_ATTEMPTED_KEY) === '1'
  } catch {
    return false
  }
}

const markFeishuAppLinkAttempted = (): void => {
  try {
    sessionStorage.setItem(FEISHU_APPLINK_ATTEMPTED_KEY, '1')
  } catch {}
}

/**
 * 移动端非飞书环境引导：在移动端浏览器（非飞书容器）打开时，提示是否用飞书打开；
 * 确认后用 applink 拉起飞书容器重新打开当前页，进入容器后即可走 H5 免登（见 use-feishu-silent-login）。
 * - 触发条件：website 目标 + 移动端 + 非飞书容器 + 已配置 App ID。不区分登录态（游客 / 已登录都提示）。
 * - 每个会话只提示一次（sessionStorage 标记）；取消不阻断页面，用户可继续在当前浏览器手动登录。
 * - applink 目标固定为「进入应用时的 URL」（如分享的 /review 链接），首帧捕获，
 *   避免随后的内部跳转（游客被导向 /login）改写掉要在飞书里打开的目标。
 */
export const useFeishuApplinkPrompt = (): void => {
  const { t } = useTranslation()
  const { confirm } = useConfirmModal()
  const entryUrl = useRef(typeof window !== 'undefined' ? window.location.href : '')

  useEffect(
    () => {
      const appId = env.VITE_FEISHU_APP_ID
      if (env.VITE_APP_TARGET !== 'website' || !isMobile() || isFeishuEnv() || !appId || hasAttemptedFeishuAppLink()) {
        return
      }

      markFeishuAppLinkAttempted()
      confirm({
        title: t('Open in Feishu?'),
        description: t('Open this page in Feishu to sign in automatically.'),
        confirmLabel: t('Open Feishu'),
        onConfirm: () => {
          window.location.href = buildFeishuAppLink(appId, entryUrl.current)
        },
      })
    },
    [],
  )
}
